// Field help for the settings screens (the Models, Sign-in and Guardrails tabs, the installation
// settings page, and the scope selector and reason box they share). Keys are the ids the screens
// pass to <Field help="…">; the `settings.<path>` entries cover every field in
// src/settings/fields.gen.ts (tests/unit/help.test.ts fails when one is missing). Copy follows
// docs/guides/settings-reference.md.
import type { HelpTopic } from "../topic";

export const SETTINGS_TOPICS = {
  "settings.sign_in": {
    title: "Sign in",
    body: "How developers sign in on the proxy.",
    example: "A group of related settings. (Sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.served_by": {
    title: "Served by",
    body: "Who serves /login and /setup* for this installation. central: nexus-web answers with the central redirects; proxy: the proxy serves sign-in itself. Set only by the flip operation, never by a settings write. The default is \"central\".",
    example: "One of central, proxy. (Served by, under Sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.sso_providers": {
    title: "Sso providers",
    body: "The identity providers people sign in with, in the order /login shows them.",
    example: "Add one entry per line. (Sso providers, under Sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.iam_keys": {
    title: "Iam keys",
    body: "Sign-in with AWS access keys presigned in the browser.",
    example: "A group of related settings. (Iam keys, under Sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.iam_keys.enabled": {
    title: "Enabled",
    body: "Enabled, one of the iam keys settings. The default is true.",
    example: "Turn it on to enable it for the installation. (Enabled, under Iam keys.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.iam_keys.require_mfa": {
    title: "Require mfa",
    body: "Require mfa, one of the iam keys settings. The default is true.",
    example: "Turn it on to enable it for the installation. (Require mfa, under Iam keys.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.cognito": {
    title: "Cognito",
    body: "Cognito, one of the sign in settings.",
    example: "A group of related settings. (Cognito, under Sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.cognito.mode": {
    title: "Mode",
    body: "disabled, create (a pool made by the IaC) or existing (user_pool_id and client_id). The default is \"create\".",
    example: "One of disabled, create, existing. (Mode, under Cognito.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.cognito.user_pool_id": {
    title: "User pool id",
    body: "User pool id, one of the cognito settings.",
    example: "Pick the value that fits the customer. (User pool id, under Cognito.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.cognito.client_id": {
    title: "Client id",
    body: "Client id, one of the cognito settings.",
    example: "Pick the value that fits the customer. (Client id, under Cognito.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.cognito.mfa": {
    title: "Mfa",
    body: "Mfa, one of the cognito settings. The default is \"optional\".",
    example: "One of off, optional, required. (Mfa, under Cognito.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.api_keys": {
    title: "Api keys",
    body: "Admin-issued keys. They are imported from the central store (source = admin); no route on the proxy mints one.",
    example: "A group of related settings. (Api keys, under Sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.api_keys.enabled": {
    title: "Enabled",
    body: "Enabled, one of the api keys settings. The default is true.",
    example: "Turn it on to enable it for the installation. (Enabled, under Api keys.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.aws_sign_in": {
    title: "Aws sign in",
    body: "The nxa_ automation sign-in: a presigned sts:GetCallerIdentity request.",
    example: "A group of related settings. (Aws sign in, under Sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.aws_sign_in.enabled": {
    title: "Enabled",
    body: "Enabled, one of the aws sign in settings. The default is true.",
    example: "Turn it on to enable it for the installation. (Enabled, under Aws sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.aws_sign_in.allowed_accounts": {
    title: "Allowed accounts",
    body: "AWS account ids, besides the installation's own, whose principals may sign in.",
    example: "Add one entry per line. (Allowed accounts, under Aws sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.aws_sign_in.allowed_principals": {
    title: "Allowed principals",
    body: "When non-empty, only principals matching one of these may sign in.",
    example: "Add one entry per line. (Allowed principals, under Aws sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.aws_sign_in.team_mappings": {
    title: "Team mappings",
    body: "Derived from identity.team_rules; kept for older proxies.",
    example: "Add one entry per line. (Team mappings, under Aws sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.key_policy": {
    title: "Key policy",
    body: "The policy of keys minted on the proxy.",
    example: "A group of related settings. (Key policy, under Sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.key_policy.lifetime_days": {
    title: "Lifetime days",
    body: "Days a minted key lives; 0 = it does not expire. The default is 0.",
    example: "A whole number of days or items. (Lifetime days, under Key policy.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.key_policy.idle_days": {
    title: "Idle days",
    body: "Days of disuse after which a key is refused and revoked; 0 = never idle. The default is 0.",
    example: "A whole number of days or items. (Idle days, under Key policy.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.key_policy.max_per_person": {
    title: "Max per person",
    body: "Live keys a person may hold, one per device. Admin-issued keys don't count. At the limit a mint is refused with 409 key_limit. The default is 10.",
    example: "A whole number of days or items. (Max per person, under Key policy.)",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.trusted_tokens": {
    title: "Trusted tokens",
    body: "Third-party JWT issuers the proxy trusts. A trusted token is never a button on /login. oidcProviders in proxy.config is derived from the entries whose mode is direct or both.",
    example: "Add one entry per line. (Trusted tokens, under Sign in.)",
    article: "users-teams-and-sign-in",
  },
  "settings.identity": {
    title: "Identity",
    body: "Team placement and onboarding for every sign-in method. Org default only: an installation override of it is refused (422 org_only).",
    example: "A group of related settings. (Identity.)",
    article: "users-teams-and-sign-in",
  },
  "settings.identity.team_rules": {
    title: "Team rules",
    body: "Tried in order after the admin pin; the first match places the person.",
    example: "Add one entry per line. (Team rules, under Identity.)",
    article: "users-teams-and-sign-in",
  },
  "settings.identity.default_team": {
    title: "Default team",
    body: "The external id of the team for a person no rule places; null leaves the team unchanged.",
    example: "Pick the value that fits the customer. (Default team, under Identity.)",
    article: "users-teams-and-sign-in",
  },
  "settings.identity.capture_attributes": {
    title: "Capture attributes",
    body: "IdP attributes kept with the person's facts, for attr: rules.",
    example: "Add one entry per line. (Capture attributes, under Identity.)",
    article: "users-teams-and-sign-in",
  },
  "settings.identity.onboarding_mode": {
    title: "Onboarding mode",
    body: "approval holds a first sign-in pending until an admin approves it. The default is \"open\".",
    example: "One of open, approval. (Onboarding mode, under Identity.)",
    article: "users-teams-and-sign-in",
  },
  "settings.admin_access": {
    title: "Admin access",
    body: "Who is a customer admin besides holders of the proxy_admin grant. Org default only.",
    example: "A group of related settings. (Admin access.)",
    article: "users-teams-and-sign-in",
  },
  "settings.admin_access.teams": {
    title: "Teams",
    body: "Members of these teams (external ids) are admins.",
    example: "Add one entry per line. (Teams, under Admin access.)",
    article: "users-teams-and-sign-in",
  },
  "settings.admin_access.match": {
    title: "Match",
    body: "People whose facts match one of these are admins. A bare * is refused.",
    example: "Add one entry per line. (Match, under Admin access.)",
    article: "users-teams-and-sign-in",
  },
  "settings.admin_access.default_admins": {
    title: "Default admins",
    body: "People who sign in with one of these verified email addresses are admins on the proxy, before any grant, team or matcher is set up. Compared without regard to case; at most 50. AllCode's proxy admins are listed by default; add your own here. Org default only.",
    example: 'A JSON list of addresses, such as ["chad.grant@allcode.com", "joel@example.com"]. (Default admins, under Admin access.)',
    article: "users-teams-and-sign-in",
  },
  "settings.models": {
    title: "Models",
    body: "Which models each tool (harness) gets. A harness is harness_of(platform): Claude Code; Codex and Codex desktop; the proxy home's chat (web); anything else. The effective list per harness is invokable ∩ servable ∩ providers ∩ selection ∩ the credential's allowedModels: harness policy is product policy, never an access boundary (SP-4).",
    example: "A group of related settings. (Models.)",
    article: "claude-code-and-codex",
  },
  "settings.models.claude_code": {
    title: "Claude Code",
    body: "Claude Code. (see the settings guide)",
    example: "A group of related settings. (Claude Code, under Models.)",
    article: "claude-code-and-codex",
  },
  "settings.models.mode": {
    title: "Selection",
    body: "Selection, one of the claude code settings. The default is \"follow_aws\".",
    example: "One of follow_aws, allow_list. (Selection, under Claude Code.)",
    article: "claude-code-and-codex",
  },
  "settings.models.providers": {
    title: "Providers",
    body: "Bedrock provider segments (anthropic, openai, amazon, meta, ...); * is every provider.",
    example: "Add one entry per line. (Providers, under Claude Code.)",
    article: "claude-code-and-codex",
  },
  "settings.models.include_new_models": {
    title: "Serve new models automatically",
    body: "Serve new models automatically, one of the claude code settings. The default is true.",
    example: "Turn it on to enable it for the installation. (Serve new models automatically, under Claude Code.)",
    article: "claude-code-and-codex",
  },
  "settings.models.allowed": {
    title: "Allowed models",
    body: "Family names (claude-opus-5-5, gpt-6-astra), as /v1/models lists them.",
    example: "Add one entry per line. (Allowed models, under Claude Code.)",
    article: "claude-code-and-codex",
  },
  "settings.models.default": {
    title: "Default model",
    body: "Listed first by /v1/models; what the tool uses when it names none. The default is \"claude-opus-5-5\".",
    example: "Pick the value that fits the customer. (Default model, under Claude Code.)",
    article: "claude-code-and-codex",
  },
  "settings.models.display_names": {
    title: "Display names",
    body: "The name Claude Code, Codex or the chat shows in its model picker. Requests still go to the same model. Under Advanced on each agent card (Models → Agents); /v1/models shows the name, routing and billing are unchanged.",
    example: "Show claude-opus-5-5 as “Opus (team default)” in Claude Code's /model picker: requests still go to Opus 5.5.",
    article: "claude-code-and-codex",
  },
  "settings.models.codex": {
    title: "Codex",
    body: "Codex and Codex desktop.",
    example: "A group of related settings. (Codex, under Models.)",
    article: "claude-code-and-codex",
  },
  "settings.models.other": {
    title: "Other tools",
    body: "Every other client: Claude desktop, Cursor, unknown user agents.",
    example: "A group of related settings. (Other tools, under Models.)",
    article: "claude-code-and-codex",
  },
  "settings.models.web": {
    title: "Web chat",
    body: "The proxy home's chat (platform web). Named on its own rather than left to other, as tool_access.DEFAULT_PLATFORM_PROVIDERS does, so narrowing other cannot silently take the chat's models away.",
    example: "A group of related settings. (Web chat, under Models.)",
    article: "claude-code-and-codex",
  },
  "settings.models.allowed_models": {
    title: "Providers",
    body: "The models the whole organisation allows, per provider. Every agent is offered only these, and each agent's default must be one of them. Follow AWS allows every model of the provider that AWS lets the account call, including ones AWS adds later. Choose models allows only the ticked ones: a model AWS adds later stays off until you tick it. Turning a provider off takes its models from every agent. Set for the whole org.",
    example: "Anthropic on Choose models with Opus 5.5 and Sonnet 5.5 ticked: when AWS adds a new Haiku, no agent gets it until you tick it in Edit.",
    article: "claude-code-and-codex",
    section: "configuring-models",
  },
  "settings.models.agents": {
    title: "Agents",
    body: "One card per agent (Claude Code, Codex, Web chat, Other tools). Each is offered the models Providers allows, shown as chips. Default model is the one the agent uses when it names none; it must be one of the allowed models. Narrow for this agent gives the agent a smaller list: fewer providers, or only some models. It can only take models away; a model the org does not allow is greyed \"not allowed globally\". Save writes every agent's changes.",
    example: "Narrow Codex to OpenAI only and its default to GPT-6: Claude Code keeps every allowed model and its own default.",
    article: "claude-code-and-codex",
    section: "configuring-models",
  },
  "settings.models.fallbacks": {
    title: "Fallbacks",
    body: "Models the proxy tries, in order, when AWS has no capacity for a model. No capacity means Bedrock answers 503 \"unable to process\" or throttles the request; a refusal, an invalid request or a guardrail block is never retried. Each fallback is checked like a new request; billing shows the model that answered. A fallback starts with a cold prompt cache, so its first answer costs more. A stream falls back only before its first word. Set for the whole org.",
    example: "When Opus 5.5 is unavailable, use Sonnet 5.5, then Haiku 5: a request for Opus that Bedrock throttles is answered by Sonnet and billed as Sonnet.",
    article: "claude-code-and-codex",
    section: "configuring-models",
  },
  "settings.guardrails": {
    title: "Guardrails",
    body: "A Bedrock guardrail the proxy attaches to requests. Off by default (SP-5). Created by the IaC when mode is create; a configured guardrail that cannot be applied fails the request closed (NFR-029). Charges for guardrail use are feature 4's concern, not this document's.",
    example: "A group of related settings. (Guardrails.)",
    article: "installations",
  },
  "settings.guardrails.enabled": {
    title: "Apply a guardrail",
    body: "Apply a guardrail, one of the guardrails settings. The default is false.",
    example: "Turn it on to enable it for the installation. (Apply a guardrail, under Guardrails.)",
    article: "installations",
  },
  "settings.guardrails.mode": {
    title: "Guardrail source",
    body: "create: the IaC builds the guardrail from definition; existing: existing names one the account already has. The default is \"create\".",
    example: "One of create, existing. (Guardrail source, under Guardrails.)",
    article: "installations",
  },
  "settings.guardrails.existing": {
    title: "Existing guardrail",
    body: "The id and version of an existing guardrail; null unless mode is existing.",
    example: "A group of related settings. (Existing guardrail, under Guardrails.)",
    article: "installations",
  },
  "settings.guardrails.existing.id": {
    title: "Id",
    body: "Id, one of the existing guardrail settings.",
    example: "Pick the value that fits the customer. (Id, under Existing guardrail.)",
    article: "installations",
  },
  "settings.guardrails.existing.version": {
    title: "Version",
    body: "A numbered version or DRAFT.",
    example: "Pick the value that fits the customer. (Version, under Existing guardrail.)",
    article: "installations",
  },
  "settings.guardrails.definition": {
    title: "Guardrail definition",
    body: "What the IaC creates when mode is create; null otherwise.",
    example: "A group of related settings. (Guardrail definition, under Guardrails.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters": {
    title: "Content filters",
    body: "Content filters, one of the guardrail definition settings.",
    example: "A group of related settings. (Content filters, under Guardrail definition.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.hate": {
    title: "Hate",
    body: "Hate, one of the content filters settings.",
    example: "A group of related settings. (Hate, under Content filters.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.hate.input": {
    title: "Input",
    body: "Input, one of the hate settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Input, under Hate.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.hate.output": {
    title: "Output",
    body: "Output, one of the hate settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Output, under Hate.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.insults": {
    title: "Insults",
    body: "Insults, one of the content filters settings.",
    example: "A group of related settings. (Insults, under Content filters.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.insults.input": {
    title: "Input",
    body: "Input, one of the insults settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Input, under Insults.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.insults.output": {
    title: "Output",
    body: "Output, one of the insults settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Output, under Insults.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.sexual": {
    title: "Sexual",
    body: "Sexual, one of the content filters settings.",
    example: "A group of related settings. (Sexual, under Content filters.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.sexual.input": {
    title: "Input",
    body: "Input, one of the sexual settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Input, under Sexual.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.sexual.output": {
    title: "Output",
    body: "Output, one of the sexual settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Output, under Sexual.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.violence": {
    title: "Violence",
    body: "Violence, one of the content filters settings.",
    example: "A group of related settings. (Violence, under Content filters.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.violence.input": {
    title: "Input",
    body: "Input, one of the violence settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Input, under Violence.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.violence.output": {
    title: "Output",
    body: "Output, one of the violence settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Output, under Violence.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.misconduct": {
    title: "Misconduct",
    body: "Misconduct, one of the content filters settings.",
    example: "A group of related settings. (Misconduct, under Content filters.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.misconduct.input": {
    title: "Input",
    body: "Input, one of the misconduct settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Input, under Misconduct.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.misconduct.output": {
    title: "Output",
    body: "Output, one of the misconduct settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Output, under Misconduct.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.prompt_attack": {
    title: "Prompt attack",
    body: "Input only: Bedrock requires the output strength NONE.",
    example: "A group of related settings. (Prompt attack, under Content filters.)",
    article: "installations",
  },
  "settings.guardrails.definition.content_filters.prompt_attack.input": {
    title: "Input",
    body: "Input, one of the prompt attack settings. The default is \"HIGH\".",
    example: "One of NONE, LOW, MEDIUM, HIGH. (Input, under Prompt attack.)",
    article: "installations",
  },
  "settings.guardrails.definition.denied_topics": {
    title: "Denied topics",
    body: "Denied topics, one of the guardrail definition settings.",
    example: "Add one entry per line. (Denied topics, under Guardrail definition.)",
    article: "installations",
  },
  "settings.guardrails.definition.word_filters": {
    title: "Word filters",
    body: "Word filters, one of the guardrail definition settings.",
    example: "A group of related settings. (Word filters, under Guardrail definition.)",
    article: "installations",
  },
  "settings.guardrails.definition.word_filters.profanity": {
    title: "Profanity",
    body: "Profanity, one of the word filters settings. The default is false.",
    example: "Turn it on to enable it for the installation. (Profanity, under Word filters.)",
    article: "installations",
  },
  "settings.guardrails.definition.word_filters.words": {
    title: "Words",
    body: "Words, one of the word filters settings.",
    example: "Add one entry per line. (Words, under Word filters.)",
    article: "installations",
  },
  "settings.guardrails.definition.sensitive_information": {
    title: "Sensitive information",
    body: "Sensitive information, one of the guardrail definition settings.",
    example: "A group of related settings. (Sensitive information, under Guardrail definition.)",
    article: "installations",
  },
  "settings.guardrails.definition.sensitive_information.pii_entities": {
    title: "Pii entities",
    body: "Pii entities, one of the sensitive information settings.",
    example: "Add one entry per line. (Pii entities, under Sensitive information.)",
    article: "installations",
  },
  "settings.guardrails.definition.sensitive_information.regexes": {
    title: "Regexes",
    body: "Regexes, one of the sensitive information settings.",
    example: "Add one entry per line. (Regexes, under Sensitive information.)",
    article: "installations",
  },
  "settings.guardrails.definition.contextual_grounding": {
    title: "Contextual grounding",
    body: "Contextual grounding, one of the guardrail definition settings.",
    example: "A group of related settings. (Contextual grounding, under Guardrail definition.)",
    article: "installations",
  },
  "settings.guardrails.definition.contextual_grounding.grounding": {
    title: "Grounding",
    body: "null leaves the check off; otherwise a confidence threshold below 1.",
    example: "undefined (Grounding, under Contextual grounding.)",
    article: "installations",
  },
  "settings.guardrails.definition.contextual_grounding.relevance": {
    title: "Relevance",
    body: "null leaves the check off; otherwise a confidence threshold below 1.",
    example: "undefined (Relevance, under Contextual grounding.)",
    article: "installations",
  },
  "settings.guardrails.definition.blocked_input_message": {
    title: "Blocked input message",
    body: "Blocked input message, one of the guardrail definition settings. The default is \"Your request was blocked by your organization's content policy.\".",
    example: "Pick the value that fits the customer. (Blocked input message, under Guardrail definition.)",
    article: "installations",
  },
  "settings.guardrails.definition.blocked_output_message": {
    title: "Blocked output message",
    body: "Blocked output message, one of the guardrail definition settings. The default is \"The response was blocked by your organization's content policy.\".",
    example: "Pick the value that fits the customer. (Blocked output message, under Guardrail definition.)",
    article: "installations",
  },
  "settings.guardrails.apply_to": {
    title: "Apply to",
    body: "The harnesses whose requests carry the guardrail; all means every one.",
    example: "Add one entry per line. (Apply to, under Guardrails.)",
    article: "installations",
  },
  "settings.guardrails.trace": {
    title: "Trace",
    body: "Whether Bedrock returns the guardrail's assessment with each response (enabled_full includes the matched text; the usage event never records it). The default is \"disabled\".",
    example: "One of disabled, enabled, enabled_full. (Trace, under Guardrails.)",
    article: "installations",
  },
  "settings.guardrails.stream_processing": {
    title: "Streaming mode",
    body: "Bedrock's streamProcessingMode: sync holds each chunk until it is assessed; async lets text through and blocks on a later assessment. The default is \"sync\".",
    example: "One of sync, async. (Streaming mode, under Guardrails.)",
    article: "installations",
  },
  "settings.upstreams": {
    title: "Upstreams",
    body: "The model providers behind the proxy. Bedrock is the only one; a later feature adds another as a new top-level section with its own owner, without a schema version change.",
    example: "A group of related settings. (Upstreams.)",
    article: "installations",
  },
  "settings.upstreams.bedrock": {
    title: "Bedrock",
    body: "Bedrock, one of the upstreams settings.",
    example: "A group of related settings. (Bedrock, under Upstreams.)",
    article: "installations",
  },
  "settings.upstreams.bedrock.enabled": {
    title: "Bedrock",
    body: "Off makes the proxy refuse every model request with 503 upstream_disabled; sign-in and the admin area keep working. The default is true.",
    example: "Turn it on to enable it for the installation. (Bedrock, under Bedrock.)",
    article: "installations",
  },
  "settings.org": {
    title: "Org",
    body: "Facts about the organisation the API fills in when it serves the document. Read-only everywhere: a settings write that carries a different value is refused (422 server_generated).",
    example: "A group of related settings. (Org.)",
    article: "installations",
  },
  "settings.org.display_name": {
    title: "Organisation",
    body: "The organisation's name as the admin app shows it; nexus-web's MeResponse.OrgName. Empty until the API serves the document. The default is \"\".",
    example: "Pick the value that fits the customer. (Organisation, under Org.)",
    article: "installations",
  },
  "settings.web_search": {
    title: "Web search",
    body: "The search provider LiteLLM runs Claude Code's web searches through (web search hotfix, 2026-09-30). Staff-only: AllCode chooses the provider and writes the key into the proxy account's Secrets Manager; the customer admin never sees these fields. provider null turns web search off.",
    example: "A group of related settings. (Web search.)",
    article: "installations",
  },
  "settings.web_search.provider": {
    title: "Search provider",
    body: "Search provider, one of the web search settings.",
    example: "One of tavily, perplexity, exa_ai, serper, brave, agentcore. (Search provider, under Web search.)",
    article: "installations",
  },
  "settings.web_search.secret_name": {
    title: "API key secret",
    body: "The Secrets Manager name of the provider's API key in the proxy account, nexus/<installationId>/web-search, never its value. Required when provider is set; the settings validator enforces the pair.",
    example: "Pick the value that fits the customer. (API key secret, under Web search.)",
    article: "installations",
  },
  "settings.quotas": {
    title: "Quotas",
    body: "How the proxy enforces the org's quotas. The quotas themselves are org data, edited on the Quotas screen; these settings say how each proxy task applies them, and which quotas the deployment pins. Every field may be pinned by the deployment.",
    example: "A group of related settings. (Quotas.)",
    article: "installations",
  },
  "settings.quotas.enforcement": {
    title: "Enforcement",
    body: "on refuses a request past a block quota with 429; shadow decides and meters every request but never refuses or warns; off neither decides nor meters. The default is \"shadow\" for the first release.",
    example: "One of on, shadow, off. (Enforcement, under Quotas.)",
    article: "installations",
  },
  "settings.quotas.sync_interval_seconds": {
    title: "Sync interval (seconds)",
    body: "Seconds between a proxy task's quota syncs while it has unsent usage; an idle task syncs at least once a minute. 1 to 60; the default is 5.",
    example: "A whole number such as 5. (Sync interval, under Quotas.)",
    article: "installations",
  },
  "settings.quotas.grace_seconds": {
    title: "Standing grace (seconds)",
    body: "How old a task's standing may be before it counts as unknown: past it, open quotas admit and closed quotas refuse with fetch_error. 60 to 86400; the default is 900.",
    example: "A whole number such as 900. (Standing grace, under Quotas.)",
    article: "installations",
  },
  "settings.quotas.cold_wait_ms": {
    title: "Cold-user wait (ms)",
    body: "Milliseconds a request from a user the task has no standing for waits on that user's first sync before it is decided without one. 0 to 1000; the default is 150.",
    example: "A whole number such as 150. (Cold-user wait, under Quotas.)",
    article: "installations",
  },
  "settings.quotas.declared": {
    title: "Declared quotas",
    body: "Quotas the deployment pins. They appear as read-only quotas of this installation in both admins and apply alongside the ones admins define (the tightest wins). Set only by the deployment, never edited here.",
    example: "A JSON list of quotas, each with name, scope, team, user, metric, basis, period, limit, thresholds_bp, enforcement, grace_bp and fail_mode. (Declared quotas, under Quotas.)",
    article: "installations",
  },
  "settings.scope": {
    title: "Scope",
    body: "Whether you are looking at the customer's default or one installation. An installation's own values override the customer default; fields it does not set follow the default.",
    example: "Choose “Whole customer” to edit the default, or one installation to see what it overrides.",
    article: "installations",
  },
  "settings.models.notSecurity": {
    title: "Model selection is not a security control",
    body: "These settings decide which models each tool is offered and which is the default. They do not stop a determined developer from naming another model; use the customer's IAM policies to forbid one outright.",
    example: "Leaving a model out of the list hides it from /v1/models, while an IAM policy denies the call.",
    article: "claude-code-and-codex",
  },
  "settings.models.inventory": {
    title: "Model inventory",
    body: "The models the installation's AWS account can reach, as the proxy last found them, with the tools each is offered to. A model not seen for 30 days drops off the list.",
    example: "A new model appears here within minutes of AWS enabling it in the account.",
    article: "installations",
  },
  "settings.pins": {
    title: "Pinned fields",
    body: "Fields the installation's deployment sets itself, with the deployment parameter that sets each. A pinned field cannot be edited here; change the parameter and redeploy.",
    example: "NEXUS_SET_GUARDRAIL_ENABLED pins guardrails on or off for the installation.",
    article: "installations",
  },
  "settings.drift": {
    title: "Drift",
    body: "Fields where what the proxy is running differs from what the settings say. Usually a proxy that has not fetched the newest settings yet; a difference that persists needs a look.",
    example: "Settings saved two minutes ago and not yet applied show as drift until the proxy reports.",
    article: "installations",
  },
  "settings.needsRedeploy": {
    title: "Needs a redeploy",
    body: "Fields whose change only takes effect when the installation is redeployed, such as the guardrail's source and definition. The value is saved; the proxy keeps the old one until then.",
    example: "Changing the guardrail definition is saved at once and applied by the next redeploy.",
    article: "installations",
  },
  "settings.sign_in.sso_providers.type": {
    title: "Provider type",
    body: "The kind of identity provider: Identity Center or another SAML provider, or an OpenID Connect provider such as Okta, Google Workspace or Entra ID. It decides which fields the form asks for.",
    example: "Okta signs in over OpenID Connect; Identity Center signs in over SAML.",
    article: "users-teams-and-sign-in",
  },
  "settings.sign_in.sso_providers.secret_name": {
    title: "Secret name",
    body: "The name of the Secrets Manager secret in the customer's AWS account that holds this provider's client secret. Nexus stores only the name, never the secret itself.",
    example: "nexus/acme-prod/sso/okta",
    article: "users-teams-and-sign-in",
  },
} satisfies Record<string, HelpTopic>;
